
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";

import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  ClipboardList,
  FileEdit,
  Globe2,
  History,
  Info,
  Radio,
  ShieldCheck,
  Flame,
  FolderTree,
  HeartPulse,
  Smartphone,
  TrendingUp,
  BadgeCheck,
  CheckCircle2,
  Eye,
  Heart,
  MapPin,
  MousePointerClick,
  RefreshCw,
  Users,
} from "lucide-react";

import UserAvatar from "../../components/UserAvatar";
import LocationViewsModal from "../../components/admin/dashboard/LocationViewsModal";

import {
  API,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  SegmentedControl,
  Skeleton,
  StatusBadge,
  apiFetch,
  cx,
} from "./_shared";

import {
  BarList,
  ChartCard,
  ColumnChart,
  KpiCard,
  Legend,
  LineChart,
  VIZ,
  fmtNum,
} from "../../components/admin/charts";

import {
  DonutChart,
  ProgressRing,
} from "../../components/admin/chartsExtra";

import {
  GRANULARITY_UNIT,
  formatBucket,
  formatFullDate,
  rangeText,
} from "../../components/admin/timeSeries";

import TrafficRangePicker, {
  DEFAULT_RANGE,
  rangeQuery,
} from "../../components/admin/TrafficRangePicker";

// =============================================
// CONSTANTS & HELPERS
// =============================================

const DEVICE_LABEL = {
  mobile: "Điện thoại",
  desktop: "Máy tính",
  tablet: "Máy tính bảng",
};

function timeAgo(iso) {
  const t = new Date(iso).getTime();

  if (!Number.isFinite(t)) return "";

  const mins = Math.max(
    0,
    Math.round((Date.now() - t) / 60000)
  );

  if (mins < 1) return "Vừa xong";
  if (mins < 60) return `${mins} phút trước`;

  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} giờ trước`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days} ngày trước`;

  return new Date(iso).toLocaleDateString("vi-VN");
}

function formatMonth(key, long) {
  const [y, m] = String(key).split("-");

  if (!y) return key;

  return long
    ? `Tháng ${Number(m)}/${y}`
    : `T${Number(m)}`;
}

const sum = (rows, key) =>
  rows.reduce(
    (a, r) => a + (Number(r[key]) || 0),
    0
  );

const pct = (cur, prev) =>
  prev > 0
    ? ((cur - prev) / prev) * 100
    : cur > 0
      ? 100
      : 0;

// =============================================
// DASHBOARD SKELETON
// =============================================

const DashboardSkeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton
          key={i}
          className="h-[150px] rounded-xl"
        />
      ))}
    </div>

    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <Skeleton className="h-[380px] rounded-xl xl:col-span-2" />
      <Skeleton className="h-[380px] rounded-xl" />
    </div>

    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <Skeleton className="h-[320px] rounded-xl xl:col-span-2" />
      <Skeleton className="h-[320px] rounded-xl" />
    </div>
  </div>
);

// =============================================
// CONTENT HEALTH CARD
// =============================================

function ContentHealthCard({ health }) {
  const items = (health?.items || []).filter(
    (i) => i.count > 0
  );

  const score = health?.score ?? 0;

  const label =
    score >= 80
      ? "Rất tốt"
      : score >= 50
        ? "Cần cải thiện"
        : "Cần chú ý";

  return (
    <ChartCard
      icon={HeartPulse}
      tone="emerald"
      title="Sức khoẻ nội dung"
      subtitle="Mức độ đầy đủ thông tin của các địa điểm"
      action={
        <Link
          to="/admin/locations"
          className="inline-flex items-center gap-1 text-xs font-semibold text-admin-primary hover:underline"
        >
          Chỉnh sửa
          <ArrowRight size={13} />
        </Link>
      }
    >
      <div className="flex items-center gap-5">
        <ProgressRing
          value={score}
          label="Điểm nội dung"
        />

        <div className="min-w-0">
          <p className="text-sm font-semibold text-admin-text">
            {label}
          </p>

          <p className="mt-1 text-xs leading-relaxed text-admin-textMuted">
            {items.length
              ? `${items.length} mục cần hoàn thiện trên ${fmtNum(health.total)} địa điểm.`
              : `Cả ${fmtNum(health.total)} địa điểm đều đầy đủ thông tin.`}
          </p>
        </div>
      </div>

      {items.length ? (
        <ul className="mt-5 space-y-3.5">
          {items.map((it) => (
            <li key={it.key}>
              <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                <span
                  className="min-w-0 truncate text-admin-text"
                  title={it.hint}
                >
                  {it.label}
                </span>

                <span className="shrink-0 text-xs font-semibold tabular-nums text-admin-text">
                  {it.count}
                  <span className="font-normal text-admin-textMuted">
                    {" "}/ {it.total}
                  </span>
                </span>
              </div>

              <div className="h-1.5 overflow-hidden rounded-full bg-admin-muted">
                <div
                  className="h-full rounded-full bg-amber-500"
                  style={{
                    width: `${Math.max(
                      4,
                      (it.count /
                        Math.max(1, it.total)) *
                        100
                    )}%`,
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-5 flex items-center gap-2 text-sm text-emerald-700">
          <CheckCircle2 size={16} />
          Không còn việc cần làm.
        </p>
      )}
    </ChartCard>
  );
}

// =============================================
// ADMIN DASHBOARD
// =============================================

/** Ô số liệu phụ (hàng dưới KPI). */
function MiniStat({ icon: Icon, color, label, value, hint, to, pulse }) {
  const body = (
    <div className="flex h-full items-center gap-3 rounded-2xl border border-admin-border bg-admin-surface px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition hover:border-admin-primary/30">
      <span className={cx("relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-sm", color)}>
        <Icon size={18} strokeWidth={2.25} />
        {pulse ? <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-admin-surface [animation:admin-live-ping_1.6s_ease-out_infinite]" /> : null}
      </span>
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-admin-textMuted">{label}</p>
        <p className="text-lg font-semibold tabular-nums leading-6 text-admin-text">{value}</p>
        {hint ? <p className="truncate text-[11px] text-admin-textMuted">{hint}</p> : null}
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
}

const TASK_TONE = {
  danger: { icon: AlertTriangle, cls: "bg-rose-600" },
  warning: { icon: AlertTriangle, cls: "bg-amber-500" },
  info: { icon: Info, cls: "bg-sky-600" },
};

const ACTION_VERB = {
  create: "thêm",
  update: "sửa",
  delete: "xoá",
  status: "đổi trạng thái",
  bulk_status: "đổi trạng thái hàng loạt",
  upload: "tải lên",
  move: "di chuyển",
  role_change: "đổi quyền",
  reset_password: "đặt lại mật khẩu",
  restore: "khôi phục",
  duplicate: "sao chép",
};
const ENTITY_LABEL = { location: "địa điểm", category: "danh mục", media: "tệp", user: "tài khoản", setting: "cài đặt" };
const ENTITY_COLOR = { location: "bg-teal-600", category: "bg-indigo-600", media: "bg-sky-600", user: "bg-violet-600", setting: "bg-slate-700" };

/** Nút cuối thẻ (dính đáy thẻ để 3 thẻ cùng hàng thẳng nhau). */
function CardFooterButton({ to, onClick, children }) {
  const cls =
    "inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-admin-border bg-admin-surface py-2 text-sm font-semibold text-admin-primary transition hover:bg-admin-primaryLight";
  return (
    <div className="mt-auto pt-4">
      {to ? (
        <Link to={to} className={cls}>
          {children}
          <ArrowRight size={14} />
        </Link>
      ) : (
        <button type="button" onClick={onClick} className={cls}>
          {children}
          <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}

const AdminDashboard = () => {
  // -----------------------------
  // STATE
  // -----------------------------

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [range, setRange] = useState(DEFAULT_RANGE);

  const [viewsOpen, setViewsOpen] = useState(false);
  const [catMode, setCatMode] = useState("views");

  const [traffic, setTraffic] = useState(null);
  const [trafficLoading, setTrafficLoading] =
    useState(true);

  // -----------------------------
  // LOAD DASHBOARD
  // -----------------------------

  const load = useCallback(
    async ({ fresh = false } = {}) => {
      setError("");

      try {
        const response = await apiFetch(
          `${API}/analytics/admin/dashboard${
            fresh ? "?refresh=1" : ""
          }`
        );

        setData(response);
      } catch (e) {
        setError(
          e?.message ||
            "Không thể tải số liệu dashboard"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  // -----------------------------
  // LOAD TRAFFIC SERIES
  // -----------------------------

  useEffect(() => {
    let alive = true;

    setTrafficLoading(true);

    apiFetch(
      `${API}/analytics/admin/traffic/series?${rangeQuery(range)}`
    )
      .then((res) => {
        if (alive) {
          setTraffic(res);
        }
      })
      .catch(() => {
        if (alive) {
          setTraffic(null);
        }
      })
      .finally(() => {
        if (alive) {
          setTrafficLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [range]);

  // -----------------------------
  // PREPARE DASHBOARD DATA
  // -----------------------------

  const view = useMemo(() => {
    const t = data?.trends || {};

    const daily = t.daily || [];
    const monthly = t.monthly || [];
    const kpi = t.kpi || {};

    const curMonth =
      monthly[monthly.length - 1]?.users || 0;

    const prevMonth =
      monthly[monthly.length - 2]?.users || 0;

    return {
      monthly,

      last30: daily.slice(-30),

      kpi: {
        ...kpi,
        newUsersDelta: pct(
          curMonth,
          prevMonth
        ),
        curMonthUsers: curMonth,
      },

      devices: (
        data?.audience?.devices || []
      ).map((d) => ({
        label:
          DEVICE_LABEL[d.label] || d.label,
        value: d.count,
      })),
    };
  }, [data]);

  const counts = data?.counts || {};
  const recent = data?.recentLocations || [];

  const points = traffic?.series || [];
  const g = traffic?.granularity || "day";

  const fmtX = (k, long) =>
    formatBucket(k, g, long);

  // -----------------------------
  // CHART SERIES
  // -----------------------------

  const trafficSeries = [
    {
      label: "Lượt xem trang",
      values: points.map(
        (d) => d.pageviews
      ),
      flags: points.map((d) =>
        d.pageviewsEstimated
          ? "ước tính"
          : null
      ),
      color: VIZ[0],
    },
    {
      label: "Khách truy cập",
      values: points.map(
        (d) => d.visitors
      ),
      color: VIZ[1],
    },
  ];

  const growthSeries = [
    {
      label: "Người dùng mới",
      values: view.monthly.map(
        (m) => m.users
      ),
      color: VIZ[0],
    },
    {
      label: "Địa điểm mới",
      values: view.monthly.map(
        (m) => m.locations
      ),
      color: VIZ[1],
    },
  ];

  // =============================================
  // RENDER
  // =============================================

  return (
    <div className="space-y-6 pb-4">
      {loading ? (
        <DashboardSkeleton />
      ) : error && !data ? (
        <Card>
          <EmptyState
            icon={AlertCircle}
            title="Không tải được dashboard"
            description={error}
            action={
              <Button
                variant="primary"
                icon={RefreshCw}
                onClick={() =>
                  load({ fresh: true })
                }
              >
                Thử lại
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          {/* ===================================
              KPI CARDS
             =================================== */}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              tone="blue"
              icon={MousePointerClick}
              label="Lượt xem trang · 30 ngày"
              value={
                view.kpi.pageviews == null
                  ? "—"
                  : fmtNum(view.kpi.pageviews)
              }
              delta={
                view.kpi.pageviewsDelta ??
                undefined
              }
              deltaLabel="so với 30 ngày trước"
              trend={view.last30.map(
                (d) => d.pageviews
              )}
            />

            <KpiCard
              tone="violet"
              icon={Users}
              label="Khách truy cập · 30 ngày"
              value={fmtNum(
                view.kpi.visitors
              )}
              delta={
                view.kpi.visitorsDelta
              }
              deltaLabel="so với 30 ngày trước"
              trend={view.last30.map(
                (d) => d.visitors
              )}
            />

            <KpiCard
              tone="amber"
              icon={Eye}
              label="Lượt xem địa điểm · 30 ngày"
              value={fmtNum(
                view.kpi.locationViews
              )}
              delta={
                view.kpi.locationViewsDelta
              }
              deltaLabel="so với 30 ngày trước"
              hint={`${fmtNum(
                counts.locationViews
              )} lượt từ trước đến nay`}
              trend={view.last30.map(
                (d) => d.locationViews
              )}
            />

            <KpiCard
              tone="rose"
              icon={Heart}
              label="Người dùng"
              value={fmtNum(
                counts.users
              )}
              delta={
                view.kpi.newUsersDelta
              }
              deltaLabel="người dùng mới so với tháng trước"
              hint={`+${fmtNum(
                view.kpi.curMonthUsers
              )} tháng này · ${fmtNum(
                counts.favorites
              )} lượt yêu thích`}
              trend={view.monthly.map(
                (m) => m.users
              )}
            />
          </div>

          {/* ===================================
              SỐ LIỆU PHỤ
             =================================== */}

          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <MiniStat icon={Radio} color="bg-emerald-600" pulse={Number(data?.live) > 0} label="Đang online" value={fmtNum(data?.live || 0)} hint="5 phút gần nhất" to="/admin/overview" />
            <MiniStat icon={Globe2} color="bg-blue-600" label="Địa điểm đã đăng" value={`${fmtNum(data?.byStatus?.published ?? counts.activeLocations ?? 0)}/${fmtNum(counts.locations || 0)}`} hint="Đang hiện trên website" to="/admin/locations?status=published" />
            <MiniStat icon={FileEdit} color="bg-slate-600" label="Bản nháp" value={fmtNum(data?.byStatus?.draft ?? counts.hiddenLocations ?? 0)} hint="Chưa hiện trên website" to="/admin/locations?status=draft" />
            <MiniStat icon={FolderTree} color="bg-indigo-600" label="Danh mục" value={fmtNum(counts.categories || 0)} to="/admin/categories" />
            <MiniStat icon={Heart} color="bg-rose-500" label="Lượt yêu thích" value={fmtNum(counts.favorites || 0)} hint={`${fmtNum(counts.favoritedLocations || 0)} địa điểm được yêu thích`} />
            <MiniStat icon={ShieldCheck} color="bg-violet-600" label="Quản trị viên" value={fmtNum(counts.admins || 0)} hint={`${fmtNum(counts.verifiedUsers || 0)}/${fmtNum(counts.users || 0)} tài khoản đã xác minh`} />
          </div>

          {/* ===================================
              TRAFFIC & CONTENT HEALTH
             =================================== */}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <ChartCard
              className="xl:col-span-2"
              icon={Activity}
              tone="blue"
              title="Lưu lượng truy cập"
              subtitle={
                traffic
                  ? `${rangeText(
                      traffic.from,
                      traffic.to
                    )} · theo ${
                      GRANULARITY_UNIT[g]
                    } · ${fmtNum(
                      sum(
                        points,
                        "pageviews"
                      )
                    )} lượt xem trang`
                  : "Đang tải…"
              }
              action={
                <TrafficRangePicker
                  value={range}
                  onChange={setRange}
                />
              }
              legend={
                <Legend
                  items={trafficSeries}
                />
              }
              table={{
                columns: [
                  g === "month"
                    ? "Tháng"
                    : g === "week"
                      ? "Tuần"
                      : "Ngày",
                  "Lượt xem trang",
                  "Khách truy cập",
                ],
                rows: points.map((d) => [
                  fmtX(d.key, true),
                  d.pageviews == null
                    ? "—"
                    : fmtNum(
                        d.pageviews
                      ),
                  fmtNum(d.visitors),
                ]),
              }}
            >
              <div
                className={cx(
                  "transition-opacity",
                  trafficLoading &&
                    "opacity-50"
                )}
              >
                {points.length ? (
                  <LineChart
                    labels={points.map(
                      (d) => d.key
                    )}
                    series={trafficSeries}
                    height={280}
                    formatLabel={fmtX}
                  />
                ) : (
                  <Skeleton className="h-[280px] w-full" />
                )}
              </div>

              {!traffic?.pageviewsEstimatedUntil && traffic?.pageviewsSince ? (
                <p className="mt-3 text-xs text-admin-textMuted">
                  Lượt xem trang được lưu từ{" "}
                  {formatFullDate(
                    traffic.pageviewsSince
                  )}
                  ; khách truy cập có dữ liệu
                  từ{" "}
                  {formatFullDate(
                    traffic.from
                  )}
                  .
                </p>
              ) : null}
            </ChartCard>

            {data?.health ? (
              <ContentHealthCard
                health={data.health}
              />
            ) : null}
          </div>

          {/* ===================================
              MONTHLY GROWTH & DEVICES
             =================================== */}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <ChartCard
              className="xl:col-span-2"
              icon={TrendingUp}
              tone="violet"
              title="Tăng trưởng theo tháng"
              subtitle={
                view.monthly.length
                  ? `Người dùng và địa điểm mới · từ ${formatMonth(
                      view.monthly[0].month,
                      true
                    ).toLowerCase()} tới nay`
                  : undefined
              }
              legend={
                <Legend
                  items={growthSeries}
                  shape="rect"
                />
              }
              table={{
                columns: [
                  "Tháng",
                  "Người dùng mới",
                  "Địa điểm mới",
                ],
                rows: view.monthly.map(
                  (m) => [
                    formatMonth(
                      m.month,
                      true
                    ),
                    fmtNum(m.users),
                    fmtNum(m.locations),
                  ]
                ),
              }}
            >
              <ColumnChart
                labels={view.monthly.map(
                  (m) => m.month
                )}
                series={growthSeries}
                height={340}
                formatLabel={formatMonth}
              />
            </ChartCard>

            <ChartCard
              icon={Smartphone}
              tone="cyan"
              title="Khách dùng thiết bị gì"
              subtitle="30 ngày gần nhất"
              action={
                <Link
                  to="/admin/overview"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-admin-primary hover:underline"
                >
                  Chi tiết
                  <ArrowRight size={13} />
                </Link>
              }
            >
              <DonutChart
                parts={view.devices}
                centerLabel="Phiên"
                size={148}
              />

              {data?.audience?.os?.length ? (
                <div className="mt-5 border-t border-admin-border pt-4">
                  <p className="mb-3 text-xs font-medium text-admin-textMuted">
                    Hệ điều hành
                  </p>

                  <BarList
                    items={data.audience.os
                      .slice(0, 4)
                      .map((o) => ({
                        key: o.label,
                        label: o.label,
                        value: o.count,
                      }))}
                  />
                </div>
              ) : null}
            </ChartCard>
          </div>

          {/* ===================================
              TOP LOCATIONS & USERS
             =================================== */}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <ChartCard
              icon={Flame}
              tone="amber"
              title="Địa điểm xem nhiều nhất"
              subtitle={`${fmtNum(
                counts.locationViews
              )} lượt xem · tất cả địa điểm`}
            >
              <div className="flex h-full flex-col">
              <BarList
                items={(
                  data?.topLocations || []
                ).slice(0, 5).map((l) => ({
                  key: l._id,
                  label: l.name,
                  value:
                    l.viewCount || 0,
                  suffix: counts.locationViews
                    ? `${(
                        Math.round(
                          ((l.viewCount || 0) /
                            counts.locationViews) *
                            1000
                        ) / 10
                      ).toLocaleString("vi-VN")}%`
                    : undefined,
                }))}
                empty="Chưa có lượt xem"
              />
              <CardFooterButton onClick={() => setViewsOpen(true)}>Xem thêm</CardFooterButton>
              </div>
            </ChartCard>

            <ChartCard
              icon={FolderTree}
              tone="emerald"
              title="Địa điểm theo danh mục"
              subtitle={
                catMode === "views"
                  ? "Tỷ lệ lượt xem — danh mục được quan tâm nhất"
                  : `${fmtNum(
                      counts.categories
                    )} danh mục · ${fmtNum(
                      counts.locations
                    )} địa điểm`
              }
              action={
                <SegmentedControl
                  value={catMode}
                  onChange={setCatMode}
                  ariaLabel="Chỉ số danh mục"
                  options={[
                    { value: "views", label: "Lượt xem" },
                    { value: "count", label: "Địa điểm" },
                  ]}
                />
              }
            >
              <div className="flex h-full flex-col">
              <BarList
                items={[
                  ...(data?.categoryBreakdown ||
                    []),
                ]
                  .sort((a, b) =>
                    catMode === "views"
                      ? (b.views || 0) -
                        (a.views || 0)
                      : b.count - a.count
                  )
                  .map((c) =>
                    catMode === "views"
                      ? {
                          key: c.name,
                          label: c.name,
                          value: c.views || 0,
                          suffix: `${(
                            c.viewPct || 0
                          ).toLocaleString("vi-VN")}%`,
                        }
                      : {
                          key: c.name,
                          label: c.name,
                          value: c.count,
                          suffix: `${c.pct}%`,
                        }
                  )}
                empty="Chưa có dữ liệu phân loại"
              />
              <CardFooterButton to="/admin/categories">Quản lý danh mục</CardFooterButton>
              </div>
            </ChartCard>

            {/* RECENT USERS */}

            <ChartCard
              icon={Users}
              tone="violet"
              title="Người dùng mới"
              subtitle={`${fmtNum(counts.verifiedUsers)}/${fmtNum(counts.users)} đã xác minh email`}
            >
              <div className="flex h-full flex-col">
                {(data?.recentUsers || []).length === 0 ? (
                  <p className="py-6 text-center text-sm text-admin-textMuted">Chưa có người dùng</p>
                ) : (
                  <ul className="space-y-3">
                    {data.recentUsers.slice(0, 5).map((u) => (
                      <li key={u._id} className="flex items-center gap-3">
                        <UserAvatar
                          url={u.avatar}
                          name={u.fullName || u.email}
                          sizeClass="h-8 w-8"
                          textClass="text-xs font-semibold"
                          roundedClass="rounded-full"
                          className="shrink-0 bg-admin-primaryLight"
                          imgClassName="object-cover"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="flex items-center gap-1 text-sm text-admin-text">
                            <span className="truncate">{u.fullName || "—"}</span>
                            {u.emailVerified ? <BadgeCheck size={14} className="shrink-0 text-sky-600" aria-label="Đã xác minh" /> : null}
                          </p>
                          <p className="truncate text-xs text-admin-textMuted">{timeAgo(u.createdAt)}</p>
                        </div>
                        <Badge tone={u.authProvider === "google" ? "info" : "neutral"}>
                          {u.authProvider === "google" ? "Google" : "Email"}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
                <CardFooterButton to="/admin/users">Xem tất cả</CardFooterButton>
              </div>
            </ChartCard>
          </div>

          {/* ===================================
              VIỆC CẦN LÀM · HOẠT ĐỘNG · YÊU THÍCH
             =================================== */}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <ChartCard icon={ClipboardList} tone="amber" title="Việc cần làm" subtitle="Những mục nên xử lý sớm">
              <div className="flex h-full flex-col">
                {(data?.tasks?.items || []).length === 0 ? (
                  <p className="flex items-center justify-center gap-2 py-6 text-sm text-emerald-700">
                    <CheckCircle2 size={16} /> Không có việc tồn đọng
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {data.tasks.items.slice(0, 5).map((t) => {
                      const tone = TASK_TONE[t.tone] || TASK_TONE.info;
                      const TIcon = tone.icon;
                      return (
                        <li key={t.key}>
                          <Link to={t.to || "#"} className="flex items-center gap-3 rounded-xl px-2 py-1.5 transition hover:bg-admin-muted">
                            <span className={cx("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white", tone.cls)}>
                              <TIcon size={15} strokeWidth={2.25} />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm text-admin-text">{t.title}</span>
                              {t.hint ? <span className="block truncate text-[11px] text-admin-textMuted">{t.hint}</span> : null}
                            </span>
                            <span className="shrink-0 rounded-full bg-admin-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-admin-text">{fmtNum(t.count)}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <CardFooterButton to="/admin/locations">Đến quản lý địa điểm</CardFooterButton>
              </div>
            </ChartCard>

            <ChartCard icon={History} tone="slate" title="Hoạt động gần đây" subtitle="Thao tác của quản trị viên">
              <div className="flex h-full flex-col">
                {(data?.recentActivity || []).length === 0 ? (
                  <p className="py-6 text-center text-sm text-admin-textMuted">Chưa có hoạt động</p>
                ) : (
                  <ul className="space-y-3">
                    {data.recentActivity.slice(0, 5).map((a) => (
                      <li key={a._id} className="flex items-start gap-3">
                        <span className={cx("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white", ENTITY_COLOR[a.entity] || "bg-slate-500")}>
                          {(a.actorName || "?").slice(0, 1).toUpperCase()}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm text-admin-text" title={a.summary}>
                            <span className="font-semibold">{a.actorName || "Hệ thống"}</span>{" "}
                            {ACTION_VERB[a.action] || a.action} {ENTITY_LABEL[a.entity] || a.entity}
                            {a.entityName ? <span className="text-admin-textMuted"> “{a.entityName}”</span> : null}
                          </p>
                          <p className="text-[11px] text-admin-textMuted">{timeAgo(a.createdAt)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <CardFooterButton to="/admin/audit">Xem nhật ký thao tác</CardFooterButton>
              </div>
            </ChartCard>

            <ChartCard
              icon={Heart}
              tone="rose"
              title="Được yêu thích nhiều nhất"
              subtitle={`${fmtNum(counts.favorites || 0)} lượt yêu thích · ${fmtNum(counts.favoritedLocations || 0)} địa điểm`}
            >
              <div className="flex h-full flex-col">
                <BarList
                  items={(data?.topFavorited || []).slice(0, 5).map((l) => ({ key: l._id, label: l.name, value: l.count, suffix: "lượt" }))}
                  color="#e11d48"
                  empty="Chưa có ai thêm yêu thích"
                />
                <CardFooterButton onClick={() => setViewsOpen(true)}>Xem lượt xem các địa điểm</CardFooterButton>
              </div>
            </ChartCard>
          </div>

          {/* ===================================
              RECENT LOCATIONS
             =================================== */}

          <Card className="overflow-hidden">
            <CardHeader
              title="Địa điểm mới thêm"
              subtitle="8 địa điểm gần nhất"
              action={
                <Link
                  to="/admin/locations"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-admin-primary hover:underline"
                >
                  Xem tất cả
                  <ArrowRight size={13} />
                </Link>
              }
            />

            {recent.length === 0 ? (
              <EmptyState
                icon={MapPin}
                title="Chưa có địa điểm"
                description="Thêm địa điểm đầu tiên để bắt đầu."
              />
            ) : (
              <div className="-mb-px mt-4 grid grid-cols-1 border-t border-admin-border md:grid-cols-2">
                {recent.map(
                  (loc, i) => (
                    <Link
                      key={loc._id}
                      to="/admin/locations"
                      className={`flex items-center gap-3 border-b border-admin-border px-5 py-3 transition hover:bg-admin-muted/60 ${
                        i % 2 === 0
                          ? "md:border-r"
                          : ""
                      }`}
                    >
                      {loc.image?.url ? (
                        <img
                          src={
                            loc.image.url
                          }
                          alt=""
                          loading="lazy"
                          className="h-10 w-10 shrink-0 rounded-lg object-cover"
                          onError={(e) => {
                            e.currentTarget.style.visibility =
                              "hidden";
                          }}
                        />
                      ) : (
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-admin-muted text-admin-textMuted">
                          <MapPin
                            size={16}
                          />
                        </span>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-admin-text">
                          {loc.name}
                        </p>

                        <p className="truncate text-xs text-admin-textMuted">
                          {loc.categoryName ||
                            "Chưa phân loại"}{" "}
                          ·{" "}
                          {timeAgo(
                            loc.createdAt
                          )}
                        </p>
                      </div>

                      <StatusBadge
                        active={
                          loc.isActive
                        }
                      />
                    </Link>
                  )
                )}
              </div>
            )}
          </Card>
        </>
      )}

      <LocationViewsModal
        open={viewsOpen}
        onClose={() => setViewsOpen(false)}
      />
    </div>
  );
};

export default AdminDashboard;
